import { createFileRoute, Link } from '@tanstack/react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger, useUserType } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  CalendarDays,
  ExternalLink,
  FileText,
  Gift,
  GraduationCap,
  HeartHandshake,
  HelpCircle,
  Lightbulb,
  MessageCircle,
  MonitorPlay,
  Rocket,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';

const LINKS = {
  readingsClub: 'https://english2empower.notion.site/p/27b1dda900ed8023956fe64787f963e8',
  recordings: 'https://drive.google.com/drive/folders/18hnFHt-_YU3jd6M3brNGKf0MSv1FkX9L',
  checklist:
    'https://english2empower.notion.site/p/Checklist-de-N-veis-English2Empower-ef0ca6e07cf34e33a0c672e11040c71f',
  usefulResources:
    'https://english2empower.notion.site/p/Useful-Resources-English2Empower-f4b77d09c7d14c9a80c10876f5d99dba',
  virtualLibrary: 'https://drive.google.com/drive/folders/1iqPUrD7U3vMuu_9menl2z2Y7neL7jd13',
  midiateca: 'https://english2empower.notion.site/p/M-diateca-E2E-17861ad81c864b0fa8a9dc55f032f732',
  podcasts:
    'https://app.notion.com/p/english2empower/Biblioteca-de-Podcasts-33d1dda900ed80dbba22d44836d23df0',
  spotify: 'https://open.spotify.com/playlist/7InmVg7OwZeDsDrftZHtmu',
  platformTutorial: 'https://english2empower.notion.site/p/2ed1dda900ed8015bc3dd14932dd4c2e',
  whatsappGroup: 'https://chat.whatsapp.com/IXfNtgpRusl9qOVPq6pkzb',
  referralRules: 'https://english2empower.notion.site/p/2a91dda900ed80f0a850e7daed94393f',
  preEnrollmentForm: 'https://english2empower.com/formulario/',
};

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  offWhite: '#FEF3EF',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// -- Responsive hook (matches journey.tsx / mural.tsx convention) --
const useWidth = () => {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 640);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

// -- Poster tilt cycle for the quick-access cards --
const QUICK_ROTATIONS = [-1.4, 1, -0.8, 1.2];

// -- Injected styles (hover/focus affordances, honoring reduced motion) --
const CENTRAL_STYLES = `
.central-quick { transition: transform .15s ease, box-shadow .15s ease; }
.central-quick:hover { transform: translateY(-3px) rotate(0deg) !important; box-shadow: 6px 10px 22px ${P.shadowDeep}; }
.central-quick:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.central-btn:hover { filter: brightness(1.06); }
.central-btn:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.central-link:hover { text-decoration: underline; }
.central-link:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.central-resource { transition: transform .12s ease, box-shadow .12s ease; }
.central-resource:hover { transform: translateY(-2px); box-shadow: 4px 8px 18px ${P.shadow}; }
.central-resource:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.central-faq summary::-webkit-details-marker { display: none; }
@media (prefers-reduced-motion: reduce) {
  .central-quick, .central-resource { transition: none; }
  .central-quick:hover { transform: none !important; }
  .central-resource:hover { transform: none; }
}
`;

// -- Shared type styles (same recipe as products/theme.ts) --
const eyebrowStyle: CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: P.maxRed,
  margin: 0,
};

const bodyStyle: CSSProperties = {
  color: P.midText,
  fontSize: 15,
  lineHeight: 1.55,
  margin: 0,
};

const itemTitleStyle: CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: P.darkText,
  margin: 0,
};

const btnRed: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  padding: '11px 18px',
  borderRadius: 4,
  border: `2px solid ${P.maxRed}`,
  background: P.maxRed,
  color: P.white,
  lineHeight: 1.2,
  textDecoration: 'none',
};

const redLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: P.maxRed,
  textDecoration: 'none',
};

const paperCardStyle: CSSProperties = {
  background: P.white,
  border: `1px solid ${P.lightBorder}`,
  borderRadius: 8,
  boxShadow: `6px 10px 22px ${P.shadow}`,
};

const innerCardStyle: CSSProperties = {
  background: P.blush,
  border: `1px solid ${P.lightBorder}`,
  borderRadius: 6,
  padding: 16,
};

type HubTab = 'school' | 'community' | 'studies';

export const Route = createFileRoute('/_app/central')({
  component: GatedCentralRoute,
});

// The full "Central da Aluna" is woman-cohort content; b2b (in-company)
// students get their own neutral-language hub, reduced to the sections their
// contract includes (guide, resources, study tips).
function GatedCentralRoute() {
  const { isB2b, isLoading } = useUserType();

  if (isLoading) {
    return null;
  }
  if (isB2b) {
    return <CentralB2bRoute />;
  }
  return <CentralRoute />;
}

function RedButton({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="central-btn" style={btnRed}>
      {label}
      <ExternalLink size={14} aria-hidden />
    </a>
  );
}

function Section({
  id,
  icon: Icon,
  title,
  children,
  isMobile,
}: {
  id?: string;
  icon: LucideIcon;
  title: string;
  children: ReactNode;
  isMobile: boolean;
}) {
  return (
    <section id={id} style={{ ...paperCardStyle, scrollMarginTop: 96, padding: isMobile ? 18 : 26 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Icon size={22} color={P.maxRed} aria-hidden />
        <h2
          style={{
            fontFamily: dmSerif,
            fontWeight: 400,
            fontSize: isMobile ? 22 : 26,
            lineHeight: 1.1,
            color: P.darkText,
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <div style={{ display: 'grid', gap: 14 }}>{children}</div>
    </section>
  );
}

function QuickCard({
  icon: Icon,
  label,
  index,
  href,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  index: number;
  href?: string;
  onClick?: () => void;
}) {
  const style: CSSProperties = {
    ...paperCardStyle,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    padding: 16,
    position: 'relative',
    transform: `rotate(${QUICK_ROTATIONS[index % QUICK_ROTATIONS.length]}deg)`,
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'left',
  };
  const body = (
    <>
      {/* washi-tape strip, same trick as the journey posters */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -8,
          left: '50%',
          width: 54,
          height: 16,
          transform: 'translateX(-50%) rotate(-3deg)',
          background: 'rgba(212, 25, 32, 0.12)',
        }}
      />
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 4,
          background: P.maxRed,
          color: P.white,
        }}
      >
        <Icon size={18} aria-hidden />
      </span>
      <span
        style={{
          fontFamily: oswald,
          fontWeight: 600,
          fontSize: 13,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: P.darkText,
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className="central-quick" style={style}>
        {body}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className="central-quick" style={style}>
      {body}
    </button>
  );
}

const tabTriggerStyle: CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  padding: '10px 16px',
  borderRadius: 4,
  border: `2px solid ${P.black}`,
  lineHeight: 1.2,
};

type GuideChapter = {
  title: string;
  paragraphs?: string[];
  items?: Array<{ text: string; example?: string }>;
  numbered?: boolean;
};

function GuideChapters({ chapters }: { chapters: GuideChapter[] }) {
  return (
    <>
      {chapters.map((chapter, chapterIdx) => (
        <details key={chapter.title} className="central-faq" style={innerCardStyle} open={chapterIdx === 0}>
          <summary style={{ cursor: 'pointer', listStyle: 'none' }}>
            <span style={{ ...itemTitleStyle, display: 'inline' }}>{chapter.title}</span>
          </summary>
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {chapter.paragraphs?.map((paragraph) => (
              <p key={paragraph} style={bodyStyle}>
                {paragraph}
              </p>
            ))}
            {chapter.items?.map((item, itemIdx) => (
              <div key={item.text} style={{ display: 'flex', gap: 10 }}>
                {chapter.numbered ? (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      background: P.maxRed,
                      color: P.white,
                      fontFamily: oswald,
                      fontWeight: 700,
                      fontSize: 12,
                      marginTop: 1,
                    }}
                  >
                    {itemIdx + 1}
                  </span>
                ) : (
                  <span style={{ color: P.maxRed, fontWeight: 700, lineHeight: 1.55 }} aria-hidden>
                    •
                  </span>
                )}
                <div>
                  <p style={bodyStyle}>{item.text}</p>
                  {item.example ? (
                    <p style={{ ...bodyStyle, marginTop: 4, fontStyle: 'italic', color: P.lightText }}>
                      {item.example}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </details>
      ))}
    </>
  );
}

function ResourceGrid({
  items,
  isMobile,
}: {
  items: Array<{ href: string; title: string; desc: string }>;
  isMobile: boolean;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
      {items.map((item) => (
        <a
          key={item.title}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className="central-resource"
          style={{ ...innerCardStyle, textDecoration: 'none', display: 'block' }}
        >
          <p style={{ ...itemTitleStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            {item.title}
            <ExternalLink size={13} color={P.maxRed} aria-hidden />
          </p>
          <p style={{ ...bodyStyle, marginTop: 6 }}>{item.desc}</p>
        </a>
      ))}
    </div>
  );
}

function TipsList({ tips }: { tips: Array<{ title: string; body: string; example: string | null }> }) {
  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
      {tips.map((tip, index) => (
        <li key={tip.title} style={{ ...innerCardStyle, display: 'flex', gap: 14 }}>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: 4,
              background: P.maxRed,
              color: P.white,
              fontFamily: oswald,
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {index + 1}
          </span>
          <div>
            <p style={itemTitleStyle}>{tip.title}</p>
            <p style={{ ...bodyStyle, marginTop: 6 }}>{tip.body}</p>
            {tip.example ? (
              <p style={{ ...bodyStyle, marginTop: 6, fontStyle: 'italic', color: P.lightText }}>{tip.example}</p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function CentralRoute() {
  const { t } = useTranslation('p2e');
  const [tab, setTab] = useState<HubTab>('community');
  const w = useWidth();
  const isMobile = w < 640;

  // These shortcuts switch tabs, then scroll once the tab content has
  // mounted (Radix mounts TabsContent on the next render).
  const goToReferral = () => {
    setTab('school');
    setTimeout(() => {
      document.getElementById('referral')?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };
  const goToResources = () => {
    setTab('studies');
    setTimeout(() => {
      document.getElementById('resources')?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };
  const goToGuide = () => {
    setTab('school');
    setTimeout(() => {
      document.getElementById('student-guide')?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };

  const extraClasses = [
    { name: t('central.extraClasses.conversationsName'), desc: t('central.extraClasses.conversationsDesc') },
    { name: t('central.extraClasses.businessName'), desc: t('central.extraClasses.businessDesc') },
    { name: t('central.extraClasses.grammarName'), desc: t('central.extraClasses.grammarDesc') },
    {
      name: t('central.extraClasses.readingsName'),
      desc: t('central.extraClasses.readingsDesc'),
      href: LINKS.readingsClub,
      linkLabel: t('central.extraClasses.readingsLink'),
    },
    { name: t('central.extraClasses.pronunciationsName'), desc: t('central.extraClasses.pronunciationsDesc') },
  ];

  const resources = [
    { href: LINKS.checklist, title: t('central.resources.checklistTitle'), desc: t('central.resources.checklistDesc') },
    { href: LINKS.usefulResources, title: t('central.resources.usefulTitle'), desc: t('central.resources.usefulDesc') },
    { href: LINKS.virtualLibrary, title: t('central.resources.libraryTitle'), desc: t('central.resources.libraryDesc') },
    { href: LINKS.midiateca, title: t('central.resources.midiatecaTitle'), desc: t('central.resources.midiatecaDesc') },
    { href: LINKS.podcasts, title: t('central.resources.podcastsTitle'), desc: t('central.resources.podcastsDesc') },
    { href: LINKS.spotify, title: t('central.resources.spotifyTitle'), desc: t('central.resources.spotifyDesc') },
  ];

  const tips = [1, 2, 3, 4, 5, 6, 7].map((n) => ({
    title: t(`central.tips.t${n}Title`),
    body: t(`central.tips.t${n}Body`),
    example: n <= 3 ? t(`central.tips.t${n}Example`) : null,
  }));

  const roleItems = [1, 2, 3, 4].map((n) => t(`central.role.r${n}`));
  const valueItems = [1, 2, 3, 4, 5].map((n) => t(`central.values.v${n}`));
  const faqItems = [
    {
      q: t('central.faq.q1'),
      intro: t('central.faq.a1Intro'),
      bullets: [1, 2, 3, 4].map((n) => t(`central.faq.a1b${n}`)),
    },
    { q: t('central.faq.q2'), intro: t('central.faq.a2'), bullets: [] },
    { q: t('central.faq.q3'), intro: t('central.faq.a3'), bullets: [] },
    { q: t('central.faq.q4'), intro: t('central.faq.a4'), bullets: [] },
  ];

  const tutoringTypes = [
    {
      title: t('central.tutoring.supportTitle'),
      note: t('central.tutoring.supportNote'),
      bullets: [1, 2, 3, 4].map((n) => t(`central.tutoring.supportB${n}`)),
    },
    {
      title: t('central.tutoring.applicationTitle'),
      note: t('central.tutoring.applicationNote'),
      bullets: [t('central.tutoring.applicationB1')],
    },
  ];

  // Student Guide chapters: paragraphs read straight through, items render as
  // a bulleted list (or numbered steps when `numbered` is set).
  const guideChapters: GuideChapter[] = [
    {
      title: t('central.guide.c1Title'),
      paragraphs: [t('central.guide.c1p1'), t('central.guide.c1p2'), t('central.guide.c1p3')],
    },
    {
      title: t('central.guide.c2Title'),
      items: [1, 2, 3, 4, 5].map((n) => ({ text: t(`central.guide.c2b${n}`) })),
    },
    {
      title: t('central.guide.c3Title'),
      paragraphs: [t('central.guide.c3Intro')],
      items: [1, 2, 3].map((n) => ({ text: t(`central.guide.c3s${n}`) })),
      numbered: true,
    },
    {
      title: t('central.guide.c4Title'),
      items: [
        { text: t('central.guide.c4b1') },
        { text: t('central.guide.c4b2'), example: t('central.guide.c4b2Example') },
        { text: t('central.guide.c4b3') },
      ],
    },
    {
      title: t('central.guide.c5Title'),
      items: [1, 2, 3, 4].map((n) => ({ text: t(`central.guide.c5b${n}`) })),
    },
    {
      title: t('central.guide.c6Title'),
      paragraphs: [t('central.guide.c6Intro')],
      items: [1, 2, 3, 4, 5, 6, 7].map((n) => ({ text: t(`central.guide.c6b${n}`) })),
    },
  ];

  const sectionGap = isMobile ? 18 : 24;

  return (
    <div style={{ minHeight: '100vh', background: P.cream, borderRadius: 12 }}>
      <style dangerouslySetInnerHTML={{ __html: CENTRAL_STYLES }} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '30px 18px 40px' : '44px 24px 56px' }}>
        {/* Title block */}
        <header style={{ marginBottom: 22 }}>
          <p style={eyebrowStyle}>{t('central.eyebrow')}</p>
          <h1
            style={{
              fontFamily: oswald,
              fontSize: isMobile ? 28 : 36,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: P.darkText,
              margin: '4px 0 6px',
              lineHeight: 1.05,
            }}
          >
            {t('central.title')}
          </h1>
          <p style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: isMobile ? 15 : 17, color: P.midText, margin: 0 }}>
            {t('central.tagline')}
          </p>
        </header>

        {/* Quick access */}
        <section
          aria-label={t('central.quick.title')}
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 14,
            marginBottom: 26,
          }}
        >
          <QuickCard icon={MonitorPlay} label={t('central.quick.tutorial')} href={LINKS.platformTutorial} index={0} />
          <QuickCard icon={FileText} label={t('central.quick.guide')} onClick={goToGuide} index={1} />
          <QuickCard icon={BookOpen} label={t('central.quick.resources')} onClick={goToResources} index={2} />
          <QuickCard icon={Gift} label={t('central.quick.referral')} onClick={goToReferral} index={3} />
        </section>

        <Tabs value={tab} onValueChange={(value) => setTab(value as HubTab)}>
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
            {(['community', 'studies', 'school'] as const).map((value) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex-1 data-[state=active]:shadow-none sm:flex-none"
                style={{
                  ...tabTriggerStyle,
                  background: tab === value ? P.black : 'transparent',
                  color: tab === value ? P.white : P.black,
                }}
              >
                {t(`central.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="school" style={{ marginTop: sectionGap, display: 'grid', gap: sectionGap }}>
            <Section id="student-guide" icon={FileText} title={t('central.guide.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.guide.desc')}</p>
              <GuideChapters chapters={guideChapters} />
            </Section>

            <Section icon={MonitorPlay} title={t('central.platform.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.platform.desc')}</p>
              <div>
                <RedButton href={LINKS.platformTutorial} label={t('central.platform.link')} />
              </div>
            </Section>

            <Section id="referral" icon={Gift} title={t('central.referral.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.referral.body1')}</p>
              <p style={{ ...bodyStyle, color: P.darkText, fontWeight: 600 }}>{t('central.referral.body2')}</p>
              <div>
                <a href={LINKS.referralRules} target="_blank" rel="noreferrer" className="central-link" style={redLinkStyle}>
                  {t('central.referral.rulesLink')}
                  <ExternalLink size={13} aria-hidden />
                </a>
              </div>
              <div style={innerCardStyle}>
                <p style={itemTitleStyle}>{t('central.referral.inviteTitle')}</p>
                <p style={{ ...bodyStyle, margin: '8px 0 12px' }}>{t('central.referral.inviteBody')}</p>
                <RedButton href={LINKS.preEnrollmentForm} label={t('central.referral.formLink')} />
              </div>
            </Section>

            <Section icon={HeartHandshake} title={t('central.values.title')} isMobile={isMobile}>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                {valueItems.map((item) => (
                  <li key={item} style={{ display: 'flex', gap: 10, ...bodyStyle, color: P.darkText }}>
                    <HeartHandshake size={16} color={P.maxRed} style={{ flexShrink: 0, marginTop: 3 }} aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </Section>

            <Section icon={HelpCircle} title={t('central.faq.title')} isMobile={isMobile}>
              {faqItems.map((item) => (
                <details key={item.q} className="central-faq" style={innerCardStyle}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      listStyle: 'none',
                      fontWeight: 600,
                      fontSize: 15,
                      color: P.darkText,
                    }}
                  >
                    {item.q}
                  </summary>
                  <p style={{ ...bodyStyle, marginTop: 10 }}>{item.intro}</p>
                  {item.bullets.length > 0 ? (
                    <ul style={{ ...bodyStyle, marginTop: 8, paddingLeft: 20, display: 'grid', gap: 6 }}>
                      {item.bullets.map((bullet) => (
                        <li key={bullet}>{bullet}</li>
                      ))}
                    </ul>
                  ) : null}
                </details>
              ))}
            </Section>
          </TabsContent>

          <TabsContent value="community" style={{ marginTop: sectionGap, display: 'grid', gap: sectionGap }}>
            <Section icon={GraduationCap} title={t('central.extraClasses.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.extraClasses.intro')}</p>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
                {extraClasses.map((item) => (
                  <li key={item.name} style={innerCardStyle}>
                    <p style={itemTitleStyle}>{item.name}</p>
                    <p style={{ ...bodyStyle, marginTop: 6 }}>{item.desc}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        className="central-link"
                        style={{ ...redLinkStyle, marginTop: 10 }}
                      >
                        {item.linkLabel}
                        <ExternalLink size={13} aria-hidden />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  ...innerCardStyle,
                  background: P.white,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <CalendarDays size={20} color={P.maxRed} style={{ flexShrink: 0 }} aria-hidden />
                <p style={{ ...bodyStyle, flex: 1, minWidth: 180 }}>{t('central.extraClasses.calendarNote')}</p>
                <Link to="/calendar" className="central-btn" style={btnRed}>
                  {t('central.extraClasses.calendarLink')}
                </Link>
              </div>
              <div
                style={{
                  ...innerCardStyle,
                  background: P.white,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <MonitorPlay size={20} color={P.maxRed} style={{ flexShrink: 0 }} aria-hidden />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <p style={itemTitleStyle}>{t('central.extraClasses.recordingsTitle')}</p>
                  <p style={{ ...bodyStyle, marginTop: 4 }}>{t('central.extraClasses.recordingsDesc')}</p>
                </div>
                <RedButton href={LINKS.recordings} label={t('central.extraClasses.recordingsLink')} />
              </div>
            </Section>

            <Section icon={MessageCircle} title={t('central.whatsapp.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.whatsapp.desc')}</p>
              <div>
                <RedButton href={LINKS.whatsappGroup} label={t('central.whatsapp.link')} />
              </div>
            </Section>

            <Section icon={Trophy} title={t('central.wall.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.wall.desc')}</p>
              <div>
                {/* The wall is native now — in-app link instead of the old Padlet. */}
                <Link to="/conquistas" className="central-btn" style={btnRed}>
                  {t('central.wall.link')}
                </Link>
              </div>
            </Section>

            <Section icon={Rocket} title={t('central.role.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.role.intro')}</p>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                {roleItems.map((item) => (
                  <li key={item} style={{ display: 'flex', gap: 10, ...bodyStyle, color: P.darkText }}>
                    <Sparkles size={16} color={P.maxRed} style={{ flexShrink: 0, marginTop: 3 }} aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
            </Section>
          </TabsContent>

          <TabsContent value="studies" style={{ marginTop: sectionGap, display: 'grid', gap: sectionGap }}>
            <Section id="resources" icon={BookOpen} title={t('central.resources.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.resources.intro')}</p>
              <ResourceGrid items={resources} isMobile={isMobile} />
            </Section>

            <Section icon={Lightbulb} title={t('central.tips.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.tips.intro')}</p>
              <TipsList tips={tips} />
            </Section>

            <Section icon={Users} title={t('central.tutoring.title')} isMobile={isMobile}>
              <p style={bodyStyle}>{t('central.tutoring.intro')}</p>
              <p style={{ ...bodyStyle, fontStyle: 'italic', color: P.lightText }}>
                {t('central.tutoring.exclusive')}
              </p>
              <p style={itemTitleStyle}>{t('central.tutoring.typesTitle')}</p>
              {tutoringTypes.map((type) => (
                <div key={type.title} style={innerCardStyle}>
                  <p style={itemTitleStyle}>{type.title}</p>
                  <p style={{ ...bodyStyle, fontStyle: 'italic', color: P.lightText, marginTop: 4 }}>{type.note}</p>
                  <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                    {type.bullets.map((bullet) => (
                      <div key={bullet} style={{ display: 'flex', gap: 10 }}>
                        <span style={{ color: P.maxRed, fontWeight: 700, lineHeight: 1.55 }} aria-hidden>
                          •
                        </span>
                        <p style={bodyStyle}>{bullet}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ ...innerCardStyle, borderColor: P.charm, background: P.white }}>
                <p style={{ ...itemTitleStyle, color: P.maxRed }}>{t('central.tutoring.attentionTitle')}</p>
                <p style={{ ...bodyStyle, marginTop: 8 }}>{t('central.tutoring.attention')}</p>
              </div>
            </Section>
          </TabsContent>
        </Tabs>

        <p style={{ fontSize: 12, color: P.lightText, lineHeight: 1.5, textAlign: 'center', marginTop: 32 }}>
          {t('central.footer')}
        </p>
      </div>
    </div>
  );
}

// B2B (in-company) hub: neutral language, no tabs, and only the sections a
// b2b contract includes — student guide, learning resources, and study tips.
// No extra classes, tutoring, referral program, WhatsApp group, or wall.
function CentralB2bRoute() {
  const { t } = useTranslation('p2e');
  const w = useWidth();
  const isMobile = w < 640;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const guideChapters: GuideChapter[] = [
    {
      title: t('centralB2b.guide.c1Title'),
      paragraphs: [t('centralB2b.guide.c1p1'), t('centralB2b.guide.c1p2'), t('centralB2b.guide.c1p3')],
    },
    {
      title: t('centralB2b.guide.c2Title'),
      items: [1, 2, 3, 4, 5].map((n) => ({ text: t(`centralB2b.guide.c2b${n}`) })),
    },
    {
      title: t('centralB2b.guide.c3Title'),
      paragraphs: [t('centralB2b.guide.c3Intro')],
      items: [1, 2].map((n) => ({ text: t(`centralB2b.guide.c3s${n}`) })),
      numbered: true,
    },
    {
      title: t('centralB2b.guide.c4Title'),
      items: [
        { text: t('centralB2b.guide.c4b1') },
        { text: t('centralB2b.guide.c4b2'), example: t('centralB2b.guide.c4b2Example') },
        { text: t('centralB2b.guide.c4b3') },
      ],
    },
    {
      title: t('centralB2b.guide.c5Title'),
      items: [1, 2, 3, 4].map((n) => ({ text: t(`centralB2b.guide.c5b${n}`) })),
    },
    {
      title: t('centralB2b.guide.c6Title'),
      paragraphs: [t('centralB2b.guide.c6Intro')],
      items: [1, 2, 3, 4, 5, 6].map((n) => ({ text: t(`centralB2b.guide.c6b${n}`) })),
    },
  ];

  const resources = [
    { href: LINKS.checklist, title: t('centralB2b.resources.checklistTitle'), desc: t('centralB2b.resources.checklistDesc') },
    { href: LINKS.usefulResources, title: t('centralB2b.resources.usefulTitle'), desc: t('centralB2b.resources.usefulDesc') },
    { href: LINKS.virtualLibrary, title: t('centralB2b.resources.libraryTitle'), desc: t('centralB2b.resources.libraryDesc') },
    { href: LINKS.midiateca, title: t('centralB2b.resources.midiatecaTitle'), desc: t('centralB2b.resources.midiatecaDesc') },
    { href: LINKS.podcasts, title: t('centralB2b.resources.podcastsTitle'), desc: t('centralB2b.resources.podcastsDesc') },
    { href: LINKS.spotify, title: t('centralB2b.resources.spotifyTitle'), desc: t('centralB2b.resources.spotifyDesc') },
  ];

  const tips = [1, 2, 3, 4, 5, 6, 7].map((n) => ({
    title: t(`centralB2b.tips.t${n}Title`),
    body: t(`centralB2b.tips.t${n}Body`),
    example: n <= 3 ? t(`centralB2b.tips.t${n}Example`) : null,
  }));

  const sectionGap = isMobile ? 18 : 24;

  return (
    <div style={{ minHeight: '100vh', background: P.cream, borderRadius: 12 }}>
      <style dangerouslySetInnerHTML={{ __html: CENTRAL_STYLES }} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '30px 18px 40px' : '44px 24px 56px' }}>
        <header style={{ marginBottom: 22 }}>
          <p style={eyebrowStyle}>{t('centralB2b.eyebrow')}</p>
          <h1
            style={{
              fontFamily: oswald,
              fontSize: isMobile ? 28 : 36,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: P.darkText,
              margin: '4px 0 6px',
              lineHeight: 1.05,
            }}
          >
            {t('centralB2b.title')}
          </h1>
          <p
            style={{ fontFamily: dmSerif, fontStyle: 'italic', fontSize: isMobile ? 15 : 17, color: P.midText, margin: 0 }}
          >
            {t('centralB2b.tagline')}
          </p>
        </header>

        <section
          aria-label={t('centralB2b.quick.title')}
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 14,
            marginBottom: 26,
          }}
        >
          <QuickCard icon={FileText} label={t('centralB2b.quick.guide')} onClick={() => scrollTo('b2b-guide')} index={0} />
          <QuickCard
            icon={BookOpen}
            label={t('centralB2b.quick.resources')}
            onClick={() => scrollTo('b2b-resources')}
            index={1}
          />
          <QuickCard icon={Lightbulb} label={t('centralB2b.quick.tips')} onClick={() => scrollTo('b2b-tips')} index={2} />
        </section>

        <div style={{ display: 'grid', gap: sectionGap }}>
          <Section id="b2b-guide" icon={FileText} title={t('centralB2b.guide.title')} isMobile={isMobile}>
            <p style={bodyStyle}>{t('centralB2b.guide.desc')}</p>
            <GuideChapters chapters={guideChapters} />
          </Section>

          <Section id="b2b-resources" icon={BookOpen} title={t('centralB2b.resources.title')} isMobile={isMobile}>
            <p style={bodyStyle}>{t('centralB2b.resources.intro')}</p>
            <ResourceGrid items={resources} isMobile={isMobile} />
          </Section>

          <Section id="b2b-tips" icon={Lightbulb} title={t('centralB2b.tips.title')} isMobile={isMobile}>
            <p style={bodyStyle}>{t('centralB2b.tips.intro')}</p>
            <TipsList tips={tips} />
          </Section>
        </div>

        <p style={{ fontSize: 12, color: P.lightText, lineHeight: 1.5, textAlign: 'center', marginTop: 32 }}>
          {t('centralB2b.footer')}
        </p>
      </div>
    </div>
  );
}
